import { ErrorLabel } from 'components/template-component/form-error-label/ErrorLabel';
import { MetaColumnType } from 'components/utility-component/wafer-map-widget/wafer-map/web-gl-utils/Enums';
// eslint-disable-next-line no-unused-vars
import { ReticleSiteAttributeMeta, StandardReticle } from 'components/utility-component/wafer-map-widget/wafer-map/web-gl-utils/Types';
import CreateDynamicColumn, { AttributeApplicationType } from 'components/wrapped-component/customized-data-grid/CreateDynamicColumn';
import CustomizedDropdown from 'components/wrapped-component/customized-dropdown/CustomizedDropdown';
import Label from 'components/wrapped-component/hint-controls/Label';
import Textbox from 'components/wrapped-component/hint-controls/Textbox';
import ModalPopup from 'components/wrapped-component/modal-popup/modal-popup';
import { CheckBox, ColorBox, SelectBox } from 'devextreme-react';
import DataGrid, {
  Column, Editing, FilterRow, Paging, RangeRule, Scrolling, StringLengthRule, Selection,
} from 'devextreme-react/data-grid';
import _ from 'lodash';
import React from 'react';
import { Button } from 'react-bootstrap';
import { UtilityFunctions } from './utility';
import { STANDARD_RETICLE_WATPCM_SITE_LIST } from './wafer-control-map';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faCopy, faTrash } from '@fortawesome/free-solid-svg-icons';
import ReticleSaveModal from './reticle-save-modal';
import toast from 'CustomToast';

type StandardReticleEditorProps = {
  onSaveStandardReticle: (data: any) => void,
  standardReticle: StandardReticle,
  reticleSizeX: number,
  reticleSizeY: number,
  autoApplyOnOpen?: boolean,
  autoApplyConfig?: {
    columnName: string;
    columnLabel: string;
    columnType: 'Text' | 'string' | 'number' | 'date' | 'boolean';
    textLength?: number;
    minValue?: number;
    maxValue?: number;
    defaultValue?: any;
  },
};

type StandardReticleEditorState = {
  standardReticle: StandardReticle | null,
  watPCMSiteError: string,
  showRecommendedFields: boolean,
  showSavePopup: boolean,
  pendingRowUpdate: {
    newData: any,
    oldData: any,
    key: any,
  } | null,
};

export default class StandardReticleEditor extends React.Component<StandardReticleEditorProps, StandardReticleEditorState> {

  private static readonly _recommendedReticleFields: string[] = [
    'PartNumber',
    'PartName',
    'DieId',
    'DieName',
    'SINF',
    'UserDefinedAttribute1',
    'UserDefinedAttribute2',
    'UserDefinedAttribute3',
  ];

  private copyToClipboard = async (pText: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(pText);
        toast.success('Copied to clipboard!');
        return;
      }
    } catch {
    }

    const lTextArea = document.createElement('textarea');
    lTextArea.value = pText;
    lTextArea.style.position = 'fixed';
    lTextArea.style.opacity = '0';
    lTextArea.style.left = '-9999px';
    document.body.appendChild(lTextArea);
    lTextArea.focus();
    lTextArea.select();
    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(lTextArea);
    }
  };

  private renderRecommendedFieldsPopover = () => {
    return (
      <div
        className="shadow-sm border rounded bg-white"
        style={{
          position: 'absolute',
          top: '28px',
          right: 0,
          width: '320px',
          zIndex: 9999,
          padding: '10px',
        }}
        onMouseEnter={this.showRecommendedFieldsPopover}
        onMouseLeave={() => { this.hideRecommendedFieldsPopoverWithDelay(900); }}
      >
        <div style={{ fontWeight: 600, marginBottom: '8px' }}>
          Recommended Reticle Site Fields
        </div>

        {StandardReticleEditor._recommendedReticleFields.map((fieldName) => (
          <div
            key={fieldName}
            className="d-flex align-items-center justify-content-between"
            style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}
          >
            <span>{fieldName}</span>

            <button
              type="button"
              className="btn btn-link p-0"
              title="Copy"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                this.copyToClipboard(fieldName);
              }}
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
          </div>
        ))}
      </div>
    );
  };


  private showRecommendedFieldsPopover = () => {
    if (this._hideRecommendedFieldsTimer) {
      window.clearTimeout(this._hideRecommendedFieldsTimer);
      this._hideRecommendedFieldsTimer = null;
    }

    if (!this.state.showRecommendedFields) {
      this.setState({ showRecommendedFields: true });
    }
  };

  private hideRecommendedFieldsPopoverWithDelay = (pDelayMs: number = 900) => {
    if (this._hideRecommendedFieldsTimer) {
      window.clearTimeout(this._hideRecommendedFieldsTimer);
    }

    this._hideRecommendedFieldsTimer = window.setTimeout(() => {
      this.setState({ showRecommendedFields: false });
      this._hideRecommendedFieldsTimer = null;
    }, pDelayMs);
  };

  datagrid: DataGrid | null = null;

  private _autoApplied = false;
  private _hideRecommendedFieldsTimer: number | null = null;

  private addAttributeToAllSites = (columnInfo: any, defaultValue?: any) => {
    this.setState((prev): StandardReticleEditorState => {
      if (!prev.standardReticle) return prev;
      const alreadyExists = prev.standardReticle.reticleSiteAttributesMeta
        .some(m => m.columnMeta.columnName === columnInfo.columnName);
      if (alreadyExists) return prev;

      const newSR = _.cloneDeep(prev.standardReticle);
      // map type
      let type = MetaColumnType.VARCHAR;
      if (columnInfo.columnType === 'number') type = MetaColumnType.DECIMAL;
      else if (columnInfo.columnType === 'date') type = MetaColumnType.DATETIME;
      else if (columnInfo.columnType === 'boolean') type = MetaColumnType.BOOL;
      // create meta
      const newMeta: ReticleSiteAttributeMeta = {
        columnMeta: {
          columnLabel: columnInfo.columnLabel,
          columnName: columnInfo.columnName,
          type,
          textLength: columnInfo.textLength,
          minValue: columnInfo.minValue,
          maxValue: columnInfo.maxValue,
          dropdownItems: [],
        },
        isApplicableForAllSites: true,
        applicableForSites: [],
      };
      newSR.reticleSiteAttributesMeta.push(newMeta);
      // ensure hasSiteAttributes and fill data values
      newSR.hasSiteAttributes = true;
      for (let i = 0; i < newSR.siteAttributesData.length; i += 1) {
        if (!Object.prototype.hasOwnProperty.call(newSR.siteAttributesData[i], columnInfo.columnName)) {
          newSR.siteAttributesData[i][columnInfo.columnName] =
            typeof defaultValue !== 'undefined' ? defaultValue : '';
        }
      }
      return { ...prev, standardReticle: newSR };
    });
  };

  constructor(props: StandardReticleEditorProps) {
    super(props);
    this.state = {
      standardReticle: null,
      watPCMSiteError: '',
      showRecommendedFields: false,
      showSavePopup: false,
      pendingRowUpdate: null,
    };
  }

  componentDidMount() {
    const { standardReticle, reticleSizeX, reticleSizeY } = this.props;
    this.setState({
      standardReticle: this.getStandardReticle(standardReticle, reticleSizeX, reticleSizeY),
    }, () => {
      const { autoApplyOnOpen, autoApplyConfig } = this.props;
      if (autoApplyOnOpen && !_.isEmpty(autoApplyConfig) && !this._autoApplied) {
        this._autoApplied = true;
        const cfg = autoApplyConfig!;
        // build the same shape CreateDynamicColumn would send
        const columnInfo = {
          columnName: cfg.columnName,
          columnLabel: cfg.columnLabel,
          columnType: cfg.columnType,
          textLength: cfg.textLength ?? 255,
          minValue: cfg.minValue ?? -10000,
          maxValue: cfg.maxValue ?? 10000,
        };
        this.addAttributeToAllSites(columnInfo, cfg.defaultValue);
      }
    });
  }

  componentDidUpdate(prevProps: StandardReticleEditorProps, prevState: StandardReticleEditorState) {
    const { standardReticle, reticleSizeX, reticleSizeY } = this.props;
    if (!_.isEqual(prevProps.standardReticle, standardReticle) || prevProps.reticleSizeX !== reticleSizeX || prevProps.reticleSizeY !== reticleSizeY) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ standardReticle: this.getStandardReticle(standardReticle, reticleSizeX, reticleSizeY) });
    }
    
    // Update backdrop z-index when modal opens
    if (this.state.showSavePopup && !prevState.showSavePopup) {
      // Find and update the backdrop z-index
      setTimeout(() => {
        const backdrop = document.querySelector('.modal-backdrop.show');
        if (backdrop) {
          (backdrop as HTMLElement).style.zIndex = '99998';
        }
        const modal = document.querySelector('.modal.reticle-save-modal.show');
        if (modal) {
          (modal as HTMLElement).style.zIndex = '99999';
        }
      }, 0);
    }
  }

  getStandardReticle = (standardReticleFromProps: StandardReticle, reticleSizeX: number, reticleSizeY: number): StandardReticle => {
    const shouldInitializeNew = standardReticleFromProps.reticle.length <= 0
      || standardReticleFromProps.reticle.length !== reticleSizeY
      || standardReticleFromProps.reticle[0].length !== reticleSizeX;
    const reticleSiteAttributesMeta: ReticleSiteAttributeMeta[] = shouldInitializeNew ? [{
      applicableForSites: [],
      isApplicableForAllSites: true,
      columnMeta: {
        columnLabel: 'Site Number',
        columnName: 'siteNumber',
        type: MetaColumnType.DECIMAL,
        dropdownItems: [],
        minValue: 1,
        maxValue: 100000000,
        textLength: 0,
      },
    }] : _.cloneDeep(standardReticleFromProps.reticleSiteAttributesMeta);
    return {
      WATPCMSite: shouldInitializeNew ? STANDARD_RETICLE_WATPCM_SITE_LIST[0][0] : standardReticleFromProps.WATPCMSite,
      reticle: shouldInitializeNew ? this.initializeNewReticle(reticleSizeX, reticleSizeY) : _.cloneDeep(standardReticleFromProps.reticle),
      hasSiteAttributes: shouldInitializeNew ? false : standardReticleFromProps.hasSiteAttributes,
      reticleSiteAttributesMeta,
      siteAttributesData: shouldInitializeNew ? this.initializeNewSiteData(reticleSizeX, reticleSizeY, reticleSiteAttributesMeta) : _.cloneDeep(standardReticleFromProps.siteAttributesData),
    };
  };

  initializeNewReticle = (reticleSizeX: number, reticleSizeY: number) => {
    const reticle: any[] = [];
    for (let retVal = 1, i = 0; i < reticleSizeY; i += 1) {
      reticle.push([]);
      for (let j = 0; j < reticleSizeX; j += 1, retVal += 1) {
        reticle[i].push(retVal);
      }
    }
    return reticle;
  };

  initializeNewSiteData = (reticleSizeX: number, reticleSizeY: number, reticleSiteAttributesMeta: ReticleSiteAttributeMeta[]) => {
    const siteAttributesData: any[] = [];
    for (let i = 1; i <= reticleSizeX * reticleSizeY; i += 1) {
      const siteAttribute: any = {};
      for (let j = 0; j < reticleSiteAttributesMeta.length; j += 1) {
        let value: any = '';
        if (reticleSiteAttributesMeta[j].columnMeta.columnName === 'siteNumber') {
          value = i;
        }
        siteAttribute[reticleSiteAttributesMeta[j].columnMeta.columnName] = value;
      }
      siteAttributesData.push(siteAttribute);
    }
    return siteAttributesData;
  };

  onChangeReticleNumbers = (value: number, flatIndex: number) => {
    const { reticleSizeX, reticleSizeY } = this.props;
    if (value <= (reticleSizeX || 0) * (reticleSizeY || 0) && value > 0) {
      this.setState((prevState: StandardReticleEditorState) => {
        const newStandardReticle = _.cloneDeep(prevState.standardReticle);
        if (!newStandardReticle) return { standardReticle: prevState.standardReticle };
        newStandardReticle.reticle[Math.floor(flatIndex / reticleSizeX)][flatIndex % reticleSizeX] = value;
        return {
          standardReticle: newStandardReticle,
        };
      });
    }
  };

  isSiteNumberRepeated = (reticle: number[][]) => {
    const isFound = new Array(reticle.length * reticle[0].length).fill(false);
    for (let i = 0; i < reticle.length; i += 1) {
      for (let j = 0; j < reticle[i].length; j += 1) {
        if (!isFound[reticle[i][j] - 1]) {
          isFound[reticle[i][j] - 1] = true;
        } else return true;
      }
    }
    return false;
  };

  areUnsavedChangesNotifiedToUser = async () => {
    let canProceed = false;
    if (this.datagrid) {
      if (!UtilityFunctions.isDataGridInEditMode(this.datagrid)) {
        canProceed = true;
      } else {
        // eslint-disable-next-line no-lonely-if
        if (await ModalPopup.confirm({
          header: 'Changes Unsaved',
          body: 'You have unsaved changes in the reticle site attributes. Do you want to proceed?',
        })) {
          canProceed = true;
        }
      }
    } else {
      canProceed = true;
    }
    return canProceed;
  };

  onRowUpdating = (e: any) => {
    // Cancel the default save
    e.cancel = true;
    
    // Store the pending update
    this.setState({
      showSavePopup: true,
      pendingRowUpdate: {
        newData: e.newData,
        oldData: e.oldData,
        key: e.key,
      },
    });
  };

  handleSaveToCurrentSite = () => {
    const { pendingRowUpdate, standardReticle } = this.state;
    if (!pendingRowUpdate || !standardReticle) return;

    this.setState((prevState: StandardReticleEditorState) => {
      if (!prevState.standardReticle) return prevState;
      
      const newSR = _.cloneDeep(prevState.standardReticle);
      const siteIndex = newSR.siteAttributesData.findIndex(
        (site: any) => site.siteNumber === pendingRowUpdate.key
      );
      
      if (siteIndex !== -1) {
        // Apply changes only to the current site
        Object.keys(pendingRowUpdate.newData).forEach((key) => {
          if (key !== 'siteNumber') {
            newSR.siteAttributesData[siteIndex][key] = pendingRowUpdate.newData[key];
          }
        });
      }
      
      return {
        ...prevState,
        standardReticle: newSR,
        showSavePopup: false,
        pendingRowUpdate: null,
      };
    }, () => {
      // Exit edit mode after save
      if (this.datagrid) {
        try {
          this.datagrid.instance.cancelEditData();
        } catch (e) {
          // If cancelEditData doesn't work, try clearing editing state
          this.datagrid.instance.option('editing.changes', []);
        }
      }
    });
  };

  handleSaveToSelectedSites = async () => {
    const { pendingRowUpdate, standardReticle } = this.state;
    if (!pendingRowUpdate || !standardReticle || !this.datagrid) return;

    const selectedRowKeys = this.datagrid.instance.getSelectedRowKeys();
    // No need to check for empty selection as button won't be shown if no sites are selected

    this.setState((prevState: StandardReticleEditorState) => {
      if (!prevState.standardReticle) return prevState;
      
      const newSR = _.cloneDeep(prevState.standardReticle);
      
      // Apply changes to all selected sites
      selectedRowKeys.forEach((siteNumber: any) => {
        const siteIndex = newSR.siteAttributesData.findIndex(
          (site: any) => site.siteNumber === siteNumber
        );
        
        if (siteIndex !== -1) {
          Object.keys(pendingRowUpdate.newData).forEach((key) => {
            if (key !== 'siteNumber') {
              newSR.siteAttributesData[siteIndex][key] = pendingRowUpdate.newData[key];
            }
          });
        }
      });
      
      return {
        ...prevState,
        standardReticle: newSR,
        showSavePopup: false,
        pendingRowUpdate: null,
      };
    }, () => {
      // Exit edit mode after save
      if (this.datagrid) {
        try {
          this.datagrid.instance.cancelEditData();
        } catch (e) {
          // If cancelEditData doesn't work, try clearing editing state
          this.datagrid.instance.option('editing.changes', []);
        }
      }
      // Show success toast message
      toast.success('The entered values were successfully applied to the chosen site numbers.');
    });
  };

  handleSaveToAllSites = () => {
    const { pendingRowUpdate, standardReticle } = this.state;
    if (!pendingRowUpdate || !standardReticle) return;

    this.setState((prevState: StandardReticleEditorState) => {
      if (!prevState.standardReticle) return prevState;
      
      const newSR = _.cloneDeep(prevState.standardReticle);
      
      // Apply changes to all sites
      newSR.siteAttributesData.forEach((site: any) => {
        Object.keys(pendingRowUpdate.newData).forEach((key) => {
          if (key !== 'siteNumber') {
            site[key] = pendingRowUpdate.newData[key];
          }
        });
      });
      
      return {
        ...prevState,
        standardReticle: newSR,
        showSavePopup: false,
        pendingRowUpdate: null,
      };
    }, () => {
      // Exit edit mode after save
      if (this.datagrid) {
        try {
          this.datagrid.instance.cancelEditData();
        } catch (e) {
          // If cancelEditData doesn't work, try clearing editing state
          this.datagrid.instance.option('editing.changes', []);
        }
      }
      // Show success toast message
      toast.success('The entered values were successfully applied to all site numbers.');
    });
  };

  handleCancelSave = () => {
    this.setState({
      showSavePopup: false,
      pendingRowUpdate: null,
    });
    // Revert changes by canceling edit
    if (this.datagrid) {
      try {
        this.datagrid.instance.cancelEditData();
      } catch (e) {
        // If cancelEditData doesn't work, try clearing editing state
        this.datagrid.instance.option('editing.changes', []);
      }
    }
  };

  onSaveStandardReticle = async () => {
    const { standardReticle } = this.state;
    const { onSaveStandardReticle } = this.props;

    if (standardReticle && !this.isSiteNumberRepeated(standardReticle.reticle)) {
      if (await this.areUnsavedChangesNotifiedToUser()) {
        this.setState({ watPCMSiteError: '' }, () => {
          onSaveStandardReticle(standardReticle);
        });
      }
    } else {
      this.setState({ watPCMSiteError: 'Site numbers can not be repeated' });
    }
  };

  onChangeStandardReticleData = (key: string, value: any) => {
    this.setState((prevState: StandardReticleEditorState) => {
      if (!prevState.standardReticle) return { standardReticle: prevState.standardReticle };
      if (key in prevState.standardReticle) {
        const newStandardReticle = _.cloneDeep(prevState.standardReticle);
        newStandardReticle[key] = value;
        return { standardReticle: newStandardReticle };
      }
      return { standardReticle: prevState.standardReticle };
    });
  };

  removeAttributeColumn = async (columnName: string, columnLabel: string) => {
    if (columnName === 'siteNumber') return;
    if (!(await this.areUnsavedChangesNotifiedToUser())) return;
    const shouldDelete = await ModalPopup.confirm({
      header: 'Remove Reticle Site Attribute',
      body: `Are you sure you want to remove "${columnLabel}" from all reticle sites?`,
    });
    if (!shouldDelete) return;
    if (this.datagrid) {
      try {
        this.datagrid.instance.cancelEditData();
      } catch (e) {
        this.datagrid.instance.option('editing.changes', []);
      }
    }

    this.setState((prevState: StandardReticleEditorState) => {
      if (!prevState.standardReticle) return prevState;
      const newStandardReticle = _.cloneDeep(prevState.standardReticle);
      newStandardReticle.reticleSiteAttributesMeta = newStandardReticle.reticleSiteAttributesMeta
        .filter((meta: ReticleSiteAttributeMeta) => meta.columnMeta.columnName !== columnName);
      newStandardReticle.siteAttributesData = newStandardReticle.siteAttributesData.map((row: any) => {
        const nextRow = { ...row };
        delete nextRow[columnName];
        return nextRow;
      });
      newStandardReticle.hasSiteAttributes = newStandardReticle.reticleSiteAttributesMeta
        .some((meta: ReticleSiteAttributeMeta) => meta.columnMeta.columnName !== 'siteNumber');
      return { ...prevState, standardReticle: newStandardReticle };
    });
  };

  getSiteAttributesDatagridColumns = () => {
    const { standardReticle } = this.state;
    return standardReticle?.reticleSiteAttributesMeta.map((field: ReticleSiteAttributeMeta, index: number) => {
      let dataType = 'string';
      if (field.columnMeta.type === MetaColumnType.DATETIME) dataType = 'date';
      else if (field.columnMeta.type === MetaColumnType.BOOL) dataType = 'boolean';
      else if (field.columnMeta.type === MetaColumnType.DECIMAL || field.columnMeta.type === MetaColumnType.LONG) dataType = 'number';
      // eslint-disable-next-line no-undef-init
      let editCellRender = undefined;
      // eslint-disable-next-line no-undef-init
      let cellRender = undefined;
      if (field.columnMeta.type === MetaColumnType.DROPDOWN) {
        editCellRender = (cell: any) => {
          return (
            <SelectBox
              defaultValue={cell.value}
              items={field.columnMeta.dropdownItems}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...cell.column.lookup}
              onValueChanged={(event) => { cell.setValue(event.value); }}
            />
          );
        };
      } else if (field.columnMeta.type === MetaColumnType.COLOR) {
        editCellRender = (cell: any) => {
          return (
            <ColorBox
              defaultValue={cell.value}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...cell.column.lookup}
              onValueChanged={(event) => { cell.setValue(event.value); }}
            />
          );
        };
        cellRender = (e: any) => {
          if (e.data) {
            return <div className="h20 mr5 mt0 ml0 mb0 " style={{ backgroundColor: (e.data[field.columnMeta.columnName]) }} />;
          }
          return null;
        };
      }
      const caption = UtilityFunctions.capitalizeFirstLetter(field.columnMeta.columnLabel);
      const headerCellRender = field.columnMeta.columnName !== 'siteNumber'
        ? () => (
          <div className="d-flex align-items-center justify-content-between w-100" style={{ gap: '6px' }}>
            <span>{caption}</span>
            <button
              type="button"
              className="btn btn-link p-0 text-danger"
              title={`Remove ${caption}`}
              onClick={(e: any) => {
                e.preventDefault();
                e.stopPropagation();
                this.removeAttributeColumn(field.columnMeta.columnName, caption);
              }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        )
        : undefined;
      return (
        <Column
          setCellValue={(newData: any, value: any) => {
            // eslint-disable-next-line no-param-reassign
            newData[field.columnMeta.columnName] = value;
          }}
          key={`${field.columnMeta.columnName}${index}`}
          dataField={field.columnMeta.columnName}
          dataType={dataType}
          caption={caption}
          headerCellRender={headerCellRender}
          editCellRender={editCellRender}
          cellRender={cellRender}
          alignment="left"
          allowEditing={field.columnMeta.columnName !== 'siteNumber'}
        >
          { dataType === 'number' && field.columnMeta.columnName !== 'siteNumber'
            ? (
              <RangeRule
                message={`Value must be between ${field.columnMeta.minValue} and ${field.columnMeta.maxValue}`}
                min={+field.columnMeta.minValue}
                max={+field.columnMeta.maxValue}
              />
            )
            : null}
          { dataType === 'string'
            ? (
              <StringLengthRule
                max={+field.columnMeta.textLength}
                message={`Value Should Not Exceed ${field.columnMeta.textLength} Characters`}
              />
            )
            : null}
        </Column>
      );
    });
  };

  render() {
    const { standardReticle, watPCMSiteError } = this.state;
    const { reticleSizeX, reticleSizeY } = this.props;
    if (!standardReticle || standardReticle.reticle.length !== reticleSizeY || standardReticle.reticle[0].length !== reticleSizeX) {
      return (
        <div />
      );
    }
    const reticleGridItems: any = [];
    let WATPCMSiteList = [['N/A', 'N/A']];
    if (standardReticle.reticle.length > 0) {
      for (let i = 0; i < (reticleSizeX || 0) * (reticleSizeY || 0); i += 1) {
        WATPCMSiteList.push([(i + 1).toString(), (i + 1).toString()]);
        const currVal = standardReticle.reticle[Math.floor(i / reticleSizeX)][i % reticleSizeX];
        reticleGridItems.push(
          <div key={i} className="reticle-grid-item" style={{ minWidth: '60px' }}>
            <Textbox
              autoComplete="off"
              type="number"
              name="1"
              style={{ border: currVal.toString() === standardReticle.WATPCMSite ? '3px solid red' : '' }}
              value={currVal}
              placeholder=""
              onChange={(e: any) => { this.onChangeReticleNumbers(+e.target.value, i); }}
            />
          </div>,
        );
      }
    }
    WATPCMSiteList = WATPCMSiteList.concat(STANDARD_RETICLE_WATPCM_SITE_LIST);
    return (
      <div>
        <div className="custom-form">
          <Label
            labelTitle="WAT/PCM Site"
            labelPosition="left-middle"
            labelSize="40"
            fieldSize="60"
            errorSize="100"
            isFieldTouched
            className="mb5"
          >
            <CustomizedDropdown
              full
              variant="clear"
              list={WATPCMSiteList}
              selectedValue={standardReticle.WATPCMSite}
              onChange={(value: any) => { this.onChangeStandardReticleData('WATPCMSite', value); }}
            />
          </Label>
          <div
            className="reticle-grid-container"
            style={{
              borderTop: standardReticle.WATPCMSite === 'Street Top' ? '3px solid red' : '',
              borderBottom: standardReticle.WATPCMSite === 'Street Bottom' ? '3px solid red' : '',
              borderLeft: standardReticle.WATPCMSite === 'Street Left' ? '3px solid red' : '',
              borderRight: standardReticle.WATPCMSite === 'Street Right' ? '3px solid red' : '',
              gridTemplateColumns: Array((reticleSizeX || 0) + 1).join('auto '),
            }}
          >
            {reticleGridItems}
          </div>
        </div>
        <ErrorLabel
          error={watPCMSiteError}
          isTouched
        />
        <div className="mt15 mb15">
          <CheckBox
            className="mr15"
            value={standardReticle.hasSiteAttributes}
            onValueChange={(value: boolean) => { this.onChangeStandardReticleData('hasSiteAttributes', value); }}
          />
          {' '}
          Reticle Site Attributes
        </div>
        <div>
          { standardReticle.hasSiteAttributes && (
            <>
              <div className="d-flex align-items-center justify-content-end" style={{ gap: '8px' }}>
                <CreateDynamicColumn
                  needExpression={false}
                  isReticleSiteAttribute
                  getColumnInfo={(columnInfo: any, attributeApplicationType?: AttributeApplicationType) => {
                    if (attributeApplicationType === AttributeApplicationType.APPLY_TO_ALL) {
                      // auto-fill empty string by default to all rows
                      this.addAttributeToAllSites(columnInfo, '');
                      return;
                    }
                    // (existing apply-to-selected path remains as-is)
                    let applicableForSites: any[] = [];
                    if (this.datagrid) {
                      applicableForSites = _.cloneDeep(this.datagrid!.instance.getSelectedRowKeys());
                    }
                    
                    // Validate that at least one row is selected for "Apply To Selected"
                    if (applicableForSites.length === 0) {
                      toast.error('Please select at least one site to apply the attribute to selected sites.');
                      return;
                    }
                    
                    this.setState((prev): StandardReticleEditorState => {
                      if (!prev.standardReticle) return prev;
                      const newSR = _.cloneDeep(prev.standardReticle);
                      let type = MetaColumnType.VARCHAR;
                      if (columnInfo.columnType === 'number') type = MetaColumnType.DECIMAL;
                      else if (columnInfo.columnType === 'date') type = MetaColumnType.DATETIME;
                      else if (columnInfo.columnType === 'boolean') type = MetaColumnType.BOOL;
                      const meta: ReticleSiteAttributeMeta = {
                        columnMeta: {
                          columnLabel: columnInfo.columnLabel,
                          columnName: columnInfo.columnName,
                          type,
                          textLength: columnInfo.textLength,
                          minValue: columnInfo.minValue,
                          maxValue: columnInfo.maxValue,
                          dropdownItems: [],
                        },
                        isApplicableForAllSites: false,
                        applicableForSites,
                      };
                      newSR.reticleSiteAttributesMeta.push(meta);
                      // Mass-fill empty string data for selected sites
                      for (let i = 0; i < newSR.siteAttributesData.length; i += 1) {
                        if (applicableForSites.includes(newSR.siteAttributesData[i].siteNumber)) {
                          if (!Object.prototype.hasOwnProperty.call(newSR.siteAttributesData[i], columnInfo.columnName)) {
                            newSR.siteAttributesData[i][columnInfo.columnName] = '';
                          }
                        }
                      }
                      newSR.hasSiteAttributes = true;
                      return { ...prev, standardReticle: newSR };
                    });
                  }}
                  buttonContent="Add Atrribute"
                />

                <div
                  className="position-relative"
                  onMouseEnter={this.showRecommendedFieldsPopover}
                  onMouseLeave={() => { this.hideRecommendedFieldsPopoverWithDelay(900); }}
                >
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    title="Recommended fields"
                    style={{ textDecoration: 'none' }}
                  >
                    <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '16px' }} />
                  </button>

                  {this.state.showRecommendedFields ? this.renderRecommendedFieldsPopover() : null}
                </div>

              </div>
              <DataGrid
                showBorders
                errorRowEnabled={false}
                columnMinWidth={100}
                ref={(datagrid: any) => { this.datagrid = datagrid; }}
                style={{ marginTop: '0px', clear: 'both' }}
                dataSource={standardReticle.siteAttributesData}
                hoverStateEnabled
                height="400px"
                width="1000px"
                allowColumnReordering
                rowAlternationEnabled
                keyExpr="siteNumber"
                showColumnLines={false}
                onRowUpdating={this.onRowUpdating}
                onRowClick={(e: any) => {
                  try {
                    const tgt = e.event && e.event.target ? e.event.target as HTMLElement : null;
                    if (!tgt) return;
                    // allow clicks that originate from the selection checkbox element
                    if (tgt.closest && tgt.closest('.dx-select-checkbox')) return;
                    // prevent row click from toggling selection
                    e.event.preventDefault();
                    e.event.stopPropagation();
                  } catch (err) {
                    // ignore errors — best-effort prevention
                  }
                }}
                onCellPrepared={(cellElement: any) => {
                  if (cellElement.rowType === 'data') {
                    const column: any = standardReticle.reticleSiteAttributesMeta.filter((c: ReticleSiteAttributeMeta) => { return c.columnMeta.columnName === cellElement.column.dataField; });
                    if (column.length > 0 && !column[0].isApplicableForAllSites && !column[0].applicableForSites.includes(cellElement.key)) {
                      // eslint-disable-next-line no-param-reassign
                      cellElement.cellElement.innerHTML = '<div style="padding-top: 10px; padding-bottom: 10px; background-color: #ccc; text-align: center;"></div>';
                    }
                  }
                }}
              >
                <FilterRow visible />
                <Scrolling mode="standard" />
                <Paging enabled={false} />
                <Selection
                  mode="multiple"
                  showCheckBoxesMode="always"
                />
                {/* Prevent row clicks from toggling selection — only checkbox clicks should select rows */}
                {this.getSiteAttributesDatagridColumns()}
                <Editing
                  useIcons
                  allowUpdating
                  confirmDelete={false}
                  mode="row"
                />
              </DataGrid>
            </>
          )}
        </div>
        <Button
          onClick={() => { this.onSaveStandardReticle(); }}
          size="sm"
          variant="primary"
          className="float-right mt10 mb30"
        >
          Apply
        </Button>
        <ReticleSaveModal
          show={this.state.showSavePopup}
          onSaveToCurrentSite={this.handleSaveToCurrentSite}
          onSaveToSelectedSites={this.handleSaveToSelectedSites}
          onSaveToAllSites={this.handleSaveToAllSites}
          onCancel={this.handleCancelSave}
          hasSelectedSites={this.datagrid ? this.datagrid.instance.getSelectedRowKeys().length > 0 : false}
        />
      </div>
    );
  }
}
